import * as React from 'react';
import { cn } from './cn';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Emerald→gold accent bar on top; lifts on hover. */
  accent?: boolean;
  /** Elevation. */
  elevation?: 'sm' | 'md' | 'none';
}

/** EDS Card / surface — Design Bible §7.2. */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, accent = false, elevation = 'sm', children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'relative rounded-eds-md border border-eds-border bg-eds-surface text-eds-fg',
        elevation === 'sm' && 'shadow-eds-sm',
        elevation === 'md' && 'shadow-eds-md',
        accent &&
          'overflow-hidden transition-transform duration-200 hover:-translate-y-0.5 motion-reduce:transform-none',
        className,
      )}
      {...props}
    >
      {accent && (
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-eds-emerald to-eds-gold"
        />
      )}
      {children}
    </div>
  ),
);
Card.displayName = 'EdsCard';

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1 p-5 sm:p-6', className)} {...props} />
  ),
);
CardHeader.displayName = 'EdsCardHeader';

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('font-eds-display text-lg font-semibold tracking-[-0.01em] text-eds-fg', className)}
      {...props}
    />
  ),
);
CardTitle.displayName = 'EdsCardTitle';

export const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('font-eds-body text-sm text-eds-muted-fg', className)} {...props} />
  ),
);
CardDescription.displayName = 'EdsCardDescription';

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-5 pt-0 sm:p-6 sm:pt-0 font-eds-body text-eds-fg', className)} {...props} />
  ),
);
CardContent.displayName = 'EdsCardContent';

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-3 p-5 pt-0 sm:p-6 sm:pt-0', className)} {...props} />
  ),
);
CardFooter.displayName = 'EdsCardFooter';

export default Card;
